import { useMemo } from 'react';
import { ALL_COUNTRIES_CODE, separators } from '../../constants';
import {
  CountryModel,
  DataFiltersModel,
  FilterPeriodType,
  IPlainObject,
} from '../../models';
import { is, transformDate } from '../../utils';

interface GetDatePeriodResult {
  dateFrom: string;
  dateTo: string;
}

function getToday(shiftDays: number): GetDatePeriodResult {
  const now = new Date();
  const start = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + shiftDays,
  );

  return {
    dateFrom: start.toISOString(),
    dateTo: start.toISOString(),
  };
}

function getLastNDays(
  shiftDays: number,
  nDays: number,
  nYears = 0,
): GetDatePeriodResult {
  const now = new Date();
  const start = new Date(
    now.getFullYear() - nYears,
    now.getMonth(),
    now.getDate() - nDays + shiftDays,
  );
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  return {
    dateFrom: start.toISOString(),
    dateTo: end.toISOString(),
  };
}

function getCurrentMonth(): GetDatePeriodResult {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  return {
    dateFrom: start.toISOString(),
    dateTo: end.toISOString(),
  };
}

function PreviousMonth(): GetDatePeriodResult {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);

  return {
    dateFrom: start.toISOString(),
    dateTo: end.toISOString(),
  };
}

function getPeriod(
  type: FilterPeriodType,
  shiftDays: number,
): Partial<GetDatePeriodResult> {
  switch (type) {
    case FilterPeriodType.Today:
      return getToday(shiftDays);
    case FilterPeriodType.Yesterday:
      return getToday((shiftDays || 0) - 1);
    case FilterPeriodType.Last3:
      return getLastNDays(shiftDays, 2);
    case FilterPeriodType.Last7:
      return getLastNDays(shiftDays, 6);
    case FilterPeriodType.Last30:
      return getLastNDays(shiftDays, 29);
    case FilterPeriodType.Last90:
      return getLastNDays(shiftDays, 89);
    case FilterPeriodType.Last180:
      return getLastNDays(shiftDays, 179);
    case FilterPeriodType.LastYear:
      return getLastNDays(shiftDays, 0, 1);
    case FilterPeriodType.CurrentMonth:
      return getCurrentMonth();
    case FilterPeriodType.PreviousMonth:
      return PreviousMonth();
    default:
      return {};
  }
}

export function getDatePeriodByType(
  type: FilterPeriodType,
  shiftDays = 0,
): Partial<GetDatePeriodResult> {
  const { dateFrom, dateTo } = getPeriod(type, shiftDays);

  if (!dateFrom || !dateTo) {
    return {};
  }

  return {
    dateFrom: transformDate(dateFrom),
    dateTo: transformDate(dateTo),
  };
}

export function validateAndGetPeriod(values: [Date, Date]): [Date, Date] {
  const [start, end] = values;

  let dateStart = start && new Date(start);
  let dateEnd = end && new Date(end);

  if (dateStart && !dateEnd) {
    dateEnd = dateStart;
  }

  if (!dateStart && dateEnd) {
    dateStart = dateEnd;
  }

  return [
    dateStart,
    new Date(dateEnd.getFullYear(), dateEnd.getMonth(), dateEnd.getDate()),
  ];
}

export function useSortCountries(
  countries?: CountryModel[],
): CountryModel[] | undefined {
  return useMemo(() => {
    if (!countries) {
      return undefined;
    }

    return countries.sort((c1, c2) => {
      if (c2.code === ALL_COUNTRIES_CODE) {
        return -1;
      }
      if (c1.code === ALL_COUNTRIES_CODE) {
        return 1;
      }

      return c1.name > c2.name ? 1 : -1;
    });
  }, [countries]);
}

export function clearUnavailableArrayItems(
  currentArray: Array<string>,
  arrayToCompare: Array<string>,
): Array<string>;
export function clearUnavailableArrayItems(
  currentArray: string,
  arrayToCompare: Array<string>,
): string;
export function clearUnavailableArrayItems(
  currentArray: Array<string> | string,
  arrayToCompare: Array<string>,
): Array<string> | string {
  if (is.array(currentArray)) {
    return currentArray.filter(
      (value: string): boolean => !!arrayToCompare.includes(value),
    );
  }

  if (is.string(currentArray)) {
    const filterArr = (currentArray as string).split(separators.comma);

    return filterArr
      .filter(
        (value: string): boolean => !!arrayToCompare.includes(value as string),
      )
      .join(separators.comma);
  }

  return currentArray;
}

export function clearUnavailableFilters(
  currentFilterValues: DataFiltersModel,
  valuesToCompare: IPlainObject,
): DataFiltersModel {
  const filters = { ...currentFilterValues };

  Object.keys(valuesToCompare).forEach((key) => {
    if (!filters[key]) {
      return;
    }

    if (is.array(valuesToCompare[key]) && filters[key]) {
      filters[key] = clearUnavailableArrayItems(
        filters[key],
        valuesToCompare[key],
      );

      return;
    }
  });

  return filters;
}
